import type { ClientRelease } from '../../shared/types/collection'
import type { TrackPreviewResult } from '../../shared/types/preview'
import { getReleasePreviews } from '../api/previews'
import { showSnackbar } from './Snackbar'

export type PreviewQueue = {
	artist: string
	releaseId: number
	releaseTitle: string
	releaseYear?: number
	tracks: NonNullable<ClientRelease['data']['tracklist']>
}

type PreparedPreviews =
	| { status: 'error' }
	| { status: 'loading' }
	| { previews: TrackPreviewResult[]; status: 'ready' }

type PreviewPlayerState = {
	isPlaying: boolean
	releaseId?: number
	trackIndex?: number
}

type PreviewAction = 'next' | 'previous' | 'toggle'

const PAUSED_PLAYER_CLOSE_DELAY_MS = 3000
const PLAYER_TRANSITION_MS = 220
const PREVIEW_PLAYER_SELECTOR = 'preview-player'
const PREVIEW_TRACK_SELECTOR = '[data-preview-track-index]'

class PreviewPlayerController {
	private disposed = false
	private readonly rootEl: HTMLElement
	private readonly audioEl: HTMLAudioElement
	private readonly artistEl: HTMLElement
	private readonly titleEl: HTMLElement
	private readonly storeLinkEl: HTMLAnchorElement
	private readonly previousButtonEl: HTMLButtonElement
	private readonly toggleButtonEl: HTMLButtonElement
	private readonly nextButtonEl: HTMLButtonElement
	private readonly progressEl: HTMLInputElement
	private readonly preparedByRelease = new Map<number, PreparedPreviews>()
	private queue: PreviewQueue | null = null
	private trackIndex = -1
	private pauseCloseTimer: number | null = null
	private hideAnimationTimer: number | null = null
	private readonly events = new AbortController()

	dispose(): void {
		this.disposed = true
		this.events.abort()
		this.audioEl.pause()
		this.audioEl.removeAttribute('src')
		this.audioEl.load()
		if (this.pauseCloseTimer !== null) clearTimeout(this.pauseCloseTimer)
		if (this.hideAnimationTimer !== null)
			clearTimeout(this.hideAnimationTimer)
		this.preparedByRelease.clear()
		this.queue = null
	}

	constructor(rootEl: HTMLElement) {
		this.rootEl = rootEl
		this.audioEl = requireElement(rootEl, '.preview-player-audio')
		this.artistEl = requireElement(rootEl, '.preview-player-artist')
		this.titleEl = requireElement(rootEl, '.preview-player-title')
		this.storeLinkEl = requireElement(rootEl, '.preview-player-store-link')
		this.previousButtonEl = requireElement(
			rootEl,
			'[data-preview-action="previous"]',
		)
		this.toggleButtonEl = requireElement(
			rootEl,
			'[data-preview-action="toggle"]',
		)
		this.nextButtonEl = requireElement(
			rootEl,
			'[data-preview-action="next"]',
		)
		this.progressEl = requireElement(rootEl, '.preview-player-progress')

		this.handlePlaying = this.handlePlaying.bind(this)
		this.handlePause = this.handlePause.bind(this)
		this.handleAudioError = this.handleAudioError.bind(this)
		this.handleControlClick = this.handleControlClick.bind(this)
		this.handleProgressInput = this.handleProgressInput.bind(this)
		this.handleTimeUpdate = this.handleTimeUpdate.bind(this)

		this.init()
	}
	private init(): void {
		this.addEventListeners()
	}
	private addEventListeners(): void {
		const { signal } = this.events
		this.audioEl.addEventListener('playing', this.handlePlaying, { signal })
		this.audioEl.addEventListener('pause', this.handlePause, { signal })
		this.audioEl.addEventListener('ended', this.handlePause, { signal })
		this.audioEl.addEventListener('error', this.handleAudioError, {
			signal,
		})
		this.audioEl.addEventListener('durationchange', this.handleTimeUpdate, {
			signal,
		})
		this.audioEl.addEventListener('timeupdate', this.handleTimeUpdate, {
			signal,
		})
		this.rootEl.addEventListener('click', this.handleControlClick, {
			signal,
		})
		this.progressEl.addEventListener('input', this.handleProgressInput, {
			signal,
		})
	}

	// Data
	prepare(queue: PreviewQueue): void {
		if (this.disposed) return
		if (this.preparedByRelease.has(queue.releaseId)) return
		this.preparedByRelease.set(queue.releaseId, { status: 'loading' })

		void getReleasePreviews(queue.releaseId, this.toPreviewRequest(queue))
			.then(({ previews }) => {
				if (this.disposed) return
				this.preparedByRelease.set(queue.releaseId, {
					previews,
					status: 'ready',
				})
			})
			.catch((error: unknown) => {
				if (this.disposed) return
				console.error('Failed to prepare release previews', error)
				this.preparedByRelease.set(queue.releaseId, { status: 'error' })
			})
	}

	// Playback
	playTrack(queue: PreviewQueue, trackIndex: number): void {
		if (this.disposed) return
		const prepared = this.preparedByRelease.get(queue.releaseId)
		if (!prepared || prepared.status === 'loading') {
			if (!prepared) this.prepare(queue)
			showSnackbar('Track previews are still loading')
			return
		}
		if (prepared.status === 'error') {
			this.preparedByRelease.delete(queue.releaseId)
			this.prepare(queue)
			showSnackbar('Could not load track previews', 'error')
			return
		}

		const preview = prepared.previews[trackIndex]
		if (!preview?.available) {
			showSnackbar('No preview is available for this track', 'error')
			return
		}

		this.clearPauseCloseTimer()
		this.audioEl.pause()
		this.queue = queue
		this.trackIndex = trackIndex
		this.titleEl.textContent = preview.title
		this.artistEl.textContent = preview.artist
		this.storeLinkEl.href = preview.storeUrl
		this.showPlayer()
		this.audioEl.src = preview.previewUrl
		this.audioEl.load()
		this.syncUi()

		// This call must stay in the original click stack for browser autoplay policy.
		void this.audioEl.play().catch((error: unknown) => {
			console.error('Failed to start track preview', error)
			this.syncUi()
			this.schedulePauseClose()
			showSnackbar('Could not play this preview', 'error')
		})
	}
	getState(): PreviewPlayerState {
		return {
			isPlaying: this.isPlaying(),
			releaseId: this.queue?.releaseId,
			trackIndex: this.trackIndex >= 0 ? this.trackIndex : undefined,
		}
	}

	private handleControlClick(event: MouseEvent): void {
		const target = event.target
		if (!(target instanceof Element)) return
		const button = target.closest<HTMLButtonElement>(
			'[data-preview-action]',
		)
		if (!button || button.disabled) return

		switch (button.dataset.previewAction as PreviewAction | undefined) {
			case 'previous':
				this.playAdjacent(-1)
				break
			case 'next':
				this.playAdjacent(1)
				break
			case 'toggle':
				this.togglePlayback()
				break
		}
	}

	private handlePlaying(): void {
		this.clearPauseCloseTimer()
		this.syncUi()
	}

	private handlePause(): void {
		this.syncUi()
		if (this.audioEl.src && !this.rootEl.hidden) {
			this.schedulePauseClose()
		}
	}

	private handleAudioError(): void {
		if (!this.audioEl.src) return
		this.syncUi()
	}

	private handleProgressInput(): void {
		const nextTime = Number(this.progressEl.value)
		if (
			!Number.isFinite(nextTime) ||
			!Number.isFinite(this.audioEl.duration)
		) {
			return
		}
		this.audioEl.currentTime = nextTime
		this.syncProgress()
		if (this.audioEl.paused) this.schedulePauseClose()
	}

	private handleTimeUpdate(): void {
		this.syncProgress()
	}

	private playAdjacent(offset: -1 | 1): void {
		if (!this.queue) return
		this.playTrack(this.queue, this.trackIndex + offset)
	}

	private togglePlayback(): void {
		if (!this.audioEl.src) return
		if (!this.audioEl.paused) {
			this.audioEl.pause()
			return
		}

		this.clearPauseCloseTimer()
		void this.audioEl.play().catch((error: unknown) => {
			console.error('Failed to resume track preview', error)
			this.schedulePauseClose()
			showSnackbar('Could not resume this preview', 'error')
		})
	}
	private closePlayer(): void {
		this.clearPauseCloseTimer()
		this.resetAudio()
		this.queue = null
		this.trackIndex = -1
		this.syncUi()
		this.hidePlayer()
	}

	private toPreviewRequest(
		queue: PreviewQueue,
	): Parameters<typeof getReleasePreviews>[1] {
		return {
			album: queue.releaseTitle,
			artist: queue.artist,
			tracks: queue.tracks.map((track) => ({
				duration: track.duration || undefined,
				title: track.title,
			})),
			year: queue.releaseYear,
		}
	}

	private resetAudio(): void {
		this.audioEl.pause()
		this.audioEl.removeAttribute('src')
		this.audioEl.load()
		this.progressEl.value = '0'
		this.progressEl.max = '0'
		this.progressEl.style.setProperty('--preview-progress', '0%')
	}

	private syncProgress(): void {
		const duration = Number.isFinite(this.audioEl.duration)
			? this.audioEl.duration
			: 0
		const currentTime = Number.isFinite(this.audioEl.currentTime)
			? this.audioEl.currentTime
			: 0
		this.progressEl.max = String(duration)
		this.progressEl.value = String(Math.min(currentTime, duration))
		this.progressEl.style.setProperty(
			'--preview-progress',
			duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
		)
	}

	private schedulePauseClose(): void {
		this.clearPauseCloseTimer()
		this.pauseCloseTimer = window.setTimeout(() => {
			this.pauseCloseTimer = null
			if (this.audioEl.paused) this.closePlayer()
		}, PAUSED_PLAYER_CLOSE_DELAY_MS)
	}

	private clearPauseCloseTimer(): void {
		if (this.pauseCloseTimer === null) return
		window.clearTimeout(this.pauseCloseTimer)
		this.pauseCloseTimer = null
	}

	private showPlayer(): void {
		this.clearHideAnimationTimer()
		this.rootEl.hidden = false
		requestAnimationFrame(() => {
			if (!this.disposed) this.rootEl.classList.add('is-visible')
		})
	}

	private hidePlayer(): void {
		this.clearHideAnimationTimer()
		this.rootEl.classList.remove('is-visible')
		this.hideAnimationTimer = window.setTimeout(() => {
			this.hideAnimationTimer = null
			this.rootEl.hidden = true
		}, PLAYER_TRANSITION_MS)
	}

	private clearHideAnimationTimer(): void {
		if (this.hideAnimationTimer === null) return
		window.clearTimeout(this.hideAnimationTimer)
		this.hideAnimationTimer = null
	}

	private syncUi(): void {
		const hasQueue = this.queue !== null
		const isPlaying = this.isPlaying()
		this.rootEl.classList.toggle('is-playing', isPlaying)
		this.toggleButtonEl.disabled = !this.audioEl.src
		this.toggleButtonEl.title = isPlaying ? 'Pause preview' : 'Play preview'
		this.previousButtonEl.disabled = !hasQueue || this.trackIndex <= 0
		this.nextButtonEl.disabled =
			!hasQueue || this.trackIndex >= (this.queue?.tracks.length ?? 0) - 1
		syncPreviewTrackRows()
	}

	private isPlaying(): boolean {
		return !this.audioEl.paused && !this.audioEl.ended
	}
}

let previewPlayer: PreviewPlayerController | null = null

export function bindPreviewPlayer(): void {
	previewPlayer?.dispose()
	if (previewPlayer) return
	const rootEl = document.getElementById(PREVIEW_PLAYER_SELECTOR)
	if (!rootEl) return
	previewPlayer = new PreviewPlayerController(rootEl)
}

export function disposePreviewPlayer(): void {
	previewPlayer?.dispose()
	previewPlayer = null
}

export function prepareTrackPreviews(queue: PreviewQueue): void {
	previewPlayer?.prepare(queue)
}

export function playTrackPreview(
	queue: PreviewQueue,
	trackIndex: number,
): void {
	previewPlayer?.playTrack(queue, trackIndex)
}

export function syncPreviewTrackRows(): void {
	const state = previewPlayer?.getState()
	document
		.querySelectorAll<HTMLButtonElement>(PREVIEW_TRACK_SELECTOR)
		.forEach((button) => {
			const isPlaying =
				Number(button.dataset.previewReleaseId) === state?.releaseId &&
				Number(button.dataset.previewTrackIndex) ===
					state?.trackIndex &&
				state.isPlaying
			button
				.closest('.track')
				?.classList.toggle('is-preview-playing', isPlaying)
		})
}

function requireElement<T extends Element>(
	rootEl: HTMLElement,
	selector: string,
): T {
	const element = rootEl.querySelector<T>(selector)
	if (!element) throw new Error(`Missing preview player element: ${selector}`)
	return element
}
